import { useState, useEffect, type ReactNode } from 'react'
import { getStoredApiKey, setStoredApiKey, healthApi } from '../utils/api'

interface AuthGateProps {
  children: ReactNode
}

type AuthState = 'checking' | 'needs_auth' | 'authenticated' | 'error'

export function AuthGate({ children }: AuthGateProps) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Check if we have a valid token on mount
  // Also check for ?token= URL parameter
  useEffect(() => {
    const checkAuth = async () => {
      // Check for token in URL first
      const urlParams = new URLSearchParams(window.location.search)
      const urlToken = urlParams.get('token')

      if (urlToken) {
        // Store the token from URL
        setStoredApiKey(urlToken)
        // Remove token from URL to avoid sharing it accidentally
        urlParams.delete('token')
        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname
        window.history.replaceState({}, '', newUrl)
      }

      const storedKey = getStoredApiKey()

      if (!storedKey) {
        setAuthState('needs_auth')
        return
      }

      try {
        await healthApi.get()
        setAuthState('authenticated')
      } catch (err: any) {
        // If 401, token is invalid - clear it and show login
        if (err.message?.includes('Unauthorized') || err.message?.includes('401')) {
          setStoredApiKey(null)
          setAuthState('needs_auth')
        } else {
          // Other errors (network, server down) - might still be authenticated
          // Let them through but show the app
          setAuthState('authenticated')
        }
      }
    }

    checkAuth()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token.trim()) return

    setIsSubmitting(true)
    setError(null)

    // Store token temporarily to test it
    setStoredApiKey(token.trim())

    try {
      await healthApi.get()
      setAuthState('authenticated')
    } catch (err: any) {
      setStoredApiKey(null)
      if (err.message?.includes('Unauthorized') || err.message?.includes('401')) {
        setError('Invalid access token')
      } else {
        setError(err.message || 'Failed to connect to server')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLogout = () => {
    setStoredApiKey(null)
    setToken('')
    setAuthState('needs_auth')
  }

  // Loading state
  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-signal-blue/30 border-t-signal-blue rounded-full animate-spin" />
          <p className="text-star-dim text-sm">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Login screen
  if (authState === 'needs_auth') {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="glass-card p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-signal-blue/20 to-signal-purple/20 flex items-center justify-center border border-signal-blue/30">
                <span className="text-2xl">🔮</span>
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-star">Cenva Forecast</h1>
                <p className="text-sm text-star-dim">Enter your access token to continue</p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="token" className="block text-sm font-mono text-star-dim mb-2">
                  Access Token
                </label>
                <input
                  id="token"
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your token..."
                  className="w-full px-4 py-3 bg-void-50 border border-star-dim/20 rounded-lg text-star placeholder:text-star-dim/50 focus:outline-none focus:border-signal-blue/50 focus:ring-2 focus:ring-signal-blue/20 transition-all"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <div className="p-3 bg-signal-red/10 border border-signal-red/30 rounded-lg">
                  <p className="text-signal-red text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!token.trim() || isSubmitting}
                className="w-full px-6 py-3 rounded-lg font-medium transition-all bg-signal-blue text-white hover:bg-signal-blue/80 disabled:bg-star-dim/20 disabled:text-star-dim disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Continue'
                )}
              </button>
            </form>

            {/* Help text */}
            <p className="mt-6 text-xs text-star-dim text-center">
              Contact your administrator if you don't have an access token.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Authenticated - render children with logout capability via context
  return (
    <AuthContext.Provider value={{ logout: handleLogout }}>
      {children}
    </AuthContext.Provider>
  )
}

// Auth context for logout functionality
import { createContext, useContext } from 'react'

interface AuthContextValue {
  logout: () => void
}

const AuthContext = createContext<AuthContextValue>({ logout: () => {} })

export function useAuth() {
  return useContext(AuthContext)
}
